import { createLogger } from '@/shared/lib/logger';
const logger = createLogger('S3Upload');

const MAX_RETRIES = 3;
const RETRY_DELAYS_MS = [1000, 2000, 4000];
// Wedged-but-open connections (flaky mobile) fire neither `error` nor `load`,
// so without a timeout the retry loop never runs and the upload modal hangs
// forever. 120s matches Apple's S3Uploader request timeout; files are ≤1.5MB
// post-compression, so a healthy link finishes orders of magnitude faster.
const REQUEST_TIMEOUT_MS = 120_000;

// Headers signed into the presigned URL must not be re-added unsigned
// (S3/R2 returns SignatureDoesNotMatch). Mirror what the server signs.
const UNSAFE_HEADERS = new Set([
  'Host',
  'Connection',
  'Content-Length',
  'Accept-Encoding',
  'User-Agent',
  'Referer',
  'Cookie',
]);

/**
 * Why an upload failed, as a code the UI can localize.
 *
 *  - network — the connection died, timed out or was aborted mid-PUT
 *  - failed  — S3/R2 answered with a non-2xx status
 *  - prepare — the file could not be decoded or re-encoded before upload
 *
 * The raw reasons ("Network error during upload", an S3 XML body, a canvas
 * failure) are English internals — they must never reach a toast in the other
 * 23 locales. Callers map the code via `localizeUploadError`. Mirrors Apple
 * `S3Error` and Android `UploadState.Error.reason`.
 */
export type UploadErrorReason = 'network' | 'failed' | 'prepare';

export class UploadError extends Error {
  constructor(
    public readonly reason: UploadErrorReason,
    /** English diagnostic — logs and Sentry only, never shown to a user. */
    message: string
  ) {
    super(message);
    this.name = 'UploadError';
  }
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

interface PresignedConfig {
  method: 'PUT';
  url: string;
  headers: Record<string, string>;
}

function performPut(
  file: File,
  config: PresignedConfig,
  onProgress?: (progress: UploadProgress) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percentage: Math.round((event.loaded / event.total) * 100),
        });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new UploadError('failed', `Upload failed with status ${xhr.status}`)
        );
      }
    });

    xhr.addEventListener('error', () => {
      reject(new UploadError('network', 'Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new UploadError('network', 'Upload aborted'));
    });

    xhr.addEventListener('timeout', () => {
      reject(new UploadError('network', 'Upload timed out'));
    });

    xhr.open(config.method, config.url);
    xhr.timeout = REQUEST_TIMEOUT_MS;
    Object.entries(config.headers).forEach(([key, value]) => {
      if (!UNSAFE_HEADERS.has(key)) {
        xhr.setRequestHeader(key, value);
      }
    });
    xhr.send(file);
  });
}

// Presigned PUT against R2 with bounded retry — matches Apple S3Uploader and
// Android S3UploadService (3 attempts, 1s/2s/4s backoff). No pause/resume.
export async function uploadWithRecovery(
  file: File,
  _legacyUrl: string,
  config: PresignedConfig,
  onProgress?: (progress: UploadProgress) => void
): Promise<void> {
  let lastError: unknown;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      logger.info(`S3 PUT attempt ${attempt + 1}, size=${file.size}`);
      await performPut(file, config, onProgress);
      return;
    } catch (error) {
      lastError = error;
      const isLast = attempt === MAX_RETRIES - 1;
      logger.warn(
        `S3 PUT failed${isLast ? ' permanently' : ''}, attempt=${attempt + 1}, size=${file.size}`,
        error
      );
      if (!isLast) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
      }
    }
  }

  throw lastError;
}
